import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { HelpTextHandler, type HelpStep } from "@/components/help";
import { LandingHeader as Header } from "@/components/landing";

export const HelpPage = () => {
  const [showHeaderButton, setShowHeaderButton] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      const heroHeight = heroRef.current?.offsetHeight || 600;
      const scrollTop = container?.scrollTop ?? window.scrollY;

      setShowHeaderButton(scrollTop > heroHeight * 0.8);
    };

    container?.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // проверка при загрузке

    return () => {
      container?.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // список шагов
  const steps1: HelpStep[] = [
    {
      id: 1,
      text: "Нажмите «Sign Up» («Создать аккаунт») вверху экрана",
      image: "/images/help/p1s1.png",
    },
    {
      id: 2,
      text: "Выберите свой регион (сервис доступен в Российской Федерации) и нажмите «Создать аккаунт»",
      image: "/images/help/p1s2.png",
    },
    {
      id: 3,
      text: "Укажите Вашу электронную почту или зарегистрируйтесь другим способом",
      image: "/images/help/p1s3.png",
    },
    {
      id: 4,
      text: "Подтвердите свою электронную почту",
      image: "/images/help/p1s4.png",
    },
    {
      id: 5,
      text: "Далее система предложит Вам ввести свой номер телефона",
      image: "/images/help/p1s5.png",
    },
    {
      id: 6,
      text: "После этого, Вам на номер телефона будет отправлен шестизначный код, введите его",
      image: "/images/help/p1s6.png",
    },
  ];
  const steps2: HelpStep[] = [
    {
      id: 1,
      text: (
        <p>
          Перейдите по{" "}
          <Link
            className="text-primary-500 underline"
            to="https://www.okx.com/ru/trade-swap"
            target="_blank"
          >
            ссылке
          </Link>{" "}
          и нажмите на шестеренку (значок настроек)
        </p>
      ),
      image: "/images/help/p2s1.png",
    },
    {
      id: 2,
      text: "Далее в списке выберите «Режим аккаунта»",
      image: "/images/help/p2s2.png",
    },
    {
      id: 3,
      text: "Выберите «Фьючерсный режим» и нажмите «Переключиться»",
      image: "/images/help/p2s3.png",
    },
  ];

  const steps3: HelpStep[] = [
    {
      id: 1,
      // TODO: поправить стрелку
      text: (
        <p>
          Для верификации личности переходим в раздел «Профиль» {"->"} «Верификация личности» или по{" "}
          <Link
            className="text-primary-500 underline"
            to="https://www.okx.com/ru/account/kyc/personal/overview"
            target="_blank"
          >
            ссылке
          </Link>
        </p>
      ),
      image: "/images/help/p3s1.png",
    },
    {
      id: 2,
      text: "Откроется раздел верификации. Пролистываем страницу вниз и кликаем «Пройти верификацию»",
      image: "/images/help/p3s2.png",
    },
    {
      id: 3,
      text: (
        <>
          <p>
            Предоставить фото документа, подтверждающего личность. На выбор — водительские права,
            паспорт гражданина РФ или загранпаспорт. Чтобы пройти верификацию, необходимо
            воспользоваться приложением или браузером на мобильном устройстве — в десктоп-версии
            появится QR-код, который ведёт на нужную страницу.
          </p>

          <ol className="mt-4 list-decimal pl-10">
            <li className="mb-4">Сделать селфи, где чётко видно лицо.</li>
            <li className="mb-4">Указать необходимую личную информацию: полное имя, адрес.</li>
            <li>
              Дождаться подтверждения от команды OKX (обычно от 30 секунд, в спорных случаях — до 96
              часов).
            </li>
          </ol>
        </>
      ),
      image: "/images/help/p3s3.png",
    },
  ];

  const steps4: HelpStep[] = [
    {
      id: 1,
      text: (
        // TODO: поправить стрелку
        <>
          <p className="mb-4">
            Для создания API-ключа переходим «Профиль» {"->"} API или по{" "}
            <Link
              className="text-primary-500 underline"
              to="https://www.okx.com/ru/account/my-api"
              target="_blank"
            >
              ссылке
            </Link>
          </p>
          <p>Нажимаем кнопку «Создать API-ключ»</p>
        </>
      ),
      image: "/images/help/p4s1.png",
    },
    {
      id: 2,
      text: (
        <>
          При создании API-ключа:{" "}
          <ul className="list-disc pl-10">
            <li>Вводим название API-ключа (любое соответствующее правилам OKX)</li>
            <li>Аккаунт (выбираем «Главный аккаунт»)</li>
            <li>Цель «Торговля API»</li>
            <li>
              Доступы: «Чтение» и «Торговля» (
              <span className="text-primary-500">«Вывод средств» не нужен!!!</span>)
            </li>
            <li>Пасс-фраза (запомните ее, необходима для SPIDER)</li>
          </ul>
          <p>Далее нажимаем «Отправить все»</p>
          <p className="mt-8">Введите подтверждающие коды из СМС и электронной почты</p>
        </>
      ),
      image: "/images/help/p4s2.png",
    },
    {
      id: 3,
      text: "Нажмите «Показать» сведения для получения API-ключа",
      image: "/images/help/p4s3.png",
    },
    {
      id: 4,
      text: (
        <>
          <p>
            Пролистайте до конца и найдите «API-ключ» и «Секретный ключ» (они понадобятся при
            добавлении API-ключа в SPIDER, скопируйте их себе в отдельное место)
          </p>
          <p className="mt-8">
            Таким образом у Вас должны быть пасс-фраза, API-ключ и секретный ключ, которые Вы будете
            использовать при добавлении ключа в SPIDER
          </p>
        </>
      ),
      image: "/images/help/p4s4.png",
    },
    {
      id: 5,
      text: (
        <>
          <p>При добавлении API в SPIDER вводим последовательно</p>
          <ul className="list-disc pl-10">
            <li>API-ключ - API-ключ (7&nbsp;пункт)</li>
            <li>Секретный ключ (API-secret) – Секретный ключ (7&nbsp;пункт)</li>
            <li>Пасс-фраза (passphrase) – Пасс-фраза, введённая в 3&#8209;м&nbsp;пункте</li>
          </ul>
        </>
      ),
      image: "/images/help/p4s5.png",
    },
  ];
  return (
    <div
      ref={containerRef}
      className="mx-auto h-screen w-full max-w-[3840px] overflow-y-auto bg-neutral-950 pt-24 pb-30"
    >
      <Header showButton={showHeaderButton} />

      <div className="flex flex-col gap-30">
        <HelpTextHandler
          botLabel="Активизация бота"
          partLabel="Часть 1"
          title="Регистрация на бирже OKX"
          steps={steps1}
        />
        <HelpTextHandler
          botLabel="Активизация бота"
          partLabel="Часть 2"
          title="Чтобы появилась возможность торговать фьючерсами, необходимо изменить тип аккаунта на бирже. Без этого шага будет создан только спотовый API-ключ, что не позволит запустить фьючерсных ботов Spider."
          steps={steps2}
        />
        <HelpTextHandler
          botLabel="Активизация бота"
          partLabel="Часть 3"
          title="Верификация личности на OKX для доступа к инструментам"
          steps={steps3}
        />
        <HelpTextHandler
          botLabel="Активизация бота"
          partLabel="Часть 4"
          title="Создание API-ключа и его подключение к сервису Spider для запуска бота"
          steps={steps4}
        />
      </div>
    </div>
  );
};
